/**
 * The old web's `ToolEditor` in its read-only, from-chat mode (item BX) —
 * what "Show Source Code" and the Save Skill card open.
 *
 * `Message.tsx` put the artifact into a `max-w-7xl h-[90vh]` dialog holding
 * `ToolEditor` with `readOnly`: the header names the skill and carries the
 * violet **Save Skill** button (green **Done** once saved); the body is the
 * details column — description card with "Revit {version} · executed at", the
 * Parameters tab with a card per input — beside the code panel (read-only
 * CodeMirror, `language · lines · characters` in the foot). Below the `xl`
 * breakpoint the two become segmented tabs, Details · Code. The chat panel and
 * the versions column are edit-mode only and are absent here, as they were.
 *
 * Saving goes through the same handler and the same saved memory as the row
 * button, so Done shows everywhere once the artifact is a skill.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { rememberSavedSkill, savedSkillName } from "../../lib/deliverArtifact";
import { artifactTarget, describeTarget, parameterLabel } from "../../lib/skills";
import { CodeView } from "../skills/CodeView";
import { formatDate } from "../skills/SkillVersionsPanel";
import { BoxIcon, ClockIcon, LoaderIcon } from "../skills/icons";
import { Modal } from "../settings/Modal";

/** The chat's "Save Skill" — the artifact becomes a v1; resolves to the saved display name. */
export type SaveSkillHandler = (artifact: Record<string, unknown>) => Promise<string>;

interface ParamLike {
  name: string;
  displayName?: string | null;
  type: string;
  description?: string | null;
  required?: boolean;
  default?: unknown;
}

function str(artifact: Record<string, unknown>, key: string): string {
  const v = artifact[key];
  return typeof v === "string" ? v : "";
}

function paramsOf(artifact: Record<string, unknown>): ParamLike[] {
  const raw = artifact["parameterSchema"];
  if (!Array.isArray(raw)) return [];
  return raw.filter((p): p is ParamLike => !!p && typeof p === "object" && typeof (p as ParamLike).name === "string");
}

/** The old ToolEditor's `xl` breakpoint: two columns above it, tabs below. */
const TWO_COLUMNS = "(min-width: 1280px)";

function useTwoColumns(): boolean {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.matchMedia(TWO_COLUMNS).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(TWO_COLUMNS);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/** lucide `CheckCircle2` — the Done state. */
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** lucide `Sparkles` — the old Save Skill button's glyph. */
function SparklesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </svg>
  );
}

export function ArtifactDialog({
  artifact,
  onSaveSkill,
  onClose,
}: {
  artifact: Record<string, unknown>;
  onSaveSkill: SaveSkillHandler | null;
  onClose: () => void;
}) {
  const { t, locale } = useLocale();
  const twoColumns = useTwoColumns();
  const [tab, setTab] = useState<"details" | "code">("details");
  // "done" is this dialog's own click: the confirmation shows once, the Done button carries the saved state after that.
  const [save, setSave] = useState<{ kind: "idle" } | { kind: "saving" } | { kind: "done" } | { kind: "error"; message: string }>({ kind: "idle" });
  const saved = savedSkillName(artifact);

  const code = str(artifact, "code");
  const name = str(artifact, "displayName").trim() || str(artifact, "name") || "bimgo-skill";
  const description = str(artifact, "description");
  const language = str(artifact, "language") || "csharp";
  // The vendor and year it ran against — Revit or AutoCAD (`artifactTarget`).
  const target = artifactTarget(artifact);
  const executedAt = str(artifact, "executionAt");
  const params = paramsOf(artifact);

  const saveButton = onSaveSkill ? (
    <button
      type="button"
      className={`settings-btn artifact-save${saved ? " is-saved" : ""}`}
      disabled={!!saved || save.kind === "saving"}
      onClick={() => {
        setSave({ kind: "saving" });
        onSaveSkill(artifact)
          .then((savedName) => {
            rememberSavedSkill(artifact, savedName);
            setSave({ kind: "done" });
          })
          .catch((e: unknown) => setSave({ kind: "error", message: e instanceof Error ? e.message : String(e) }));
      }}
    >
      {saved ? <CheckIcon /> : save.kind === "saving" ? <LoaderIcon size={14} /> : <SparklesIcon />}
      {saved ? t("toolEditor.done") : save.kind === "saving" ? t("toolEditor.saving") : t("message.saveSkill")}
    </button>
  ) : null;

  const details = (
    <div className="skill-editor-details artifact-details">
      <div className="skill-desc-card">
        <h4>{t("toolEditor.description")}</h4>
        <p className="skill-desc-text">{description || t("toolEditor.noDescription")}</p>
        {/* Two rows, as on the skill page: what it ran on, then when — in the skill page's
            date format rather than the browser's full date and seconds. */}
        <div className="skill-desc-meta">
          <div className="skill-card-meta skill-desc-meta-row">
            <BoxIcon size={12} />
            <span>{target ? describeTarget(target) : t("skills.unknownVersion")}</span>
          </div>
          <div className="skill-card-meta skill-desc-meta-row">
            <ClockIcon size={12} />
            <span>
              {t("toolEditor.executedAt")} {formatDate(executedAt || null, locale)}
            </span>
          </div>
        </div>
      </div>
      {params.length > 0 ? (
        <>
          <div className="skill-tabs" role="tablist">
            <button type="button" role="tab" aria-selected="true" className="skill-tab is-active">
              {t("toolEditor.parameters")} {params.length}
            </button>
          </div>
          <div className="skill-tab-body">
            <div className="param-list">
              {params.map((param, index) => (
                <div className="param-card" key={`${param.name}-${index}`}>
                  <div className="param-head">
                    <div>
                      <span className="param-name">{parameterLabel(param)}</span>
                      {param.required ? <span className="param-required">*</span> : null}
                      {param.description ? <p className="param-desc">{param.description}</p> : null}
                    </div>
                    <span className="param-type">{param.type}</span>
                  </div>
                  {param.default !== undefined && param.default !== null ? (
                    <input className="settings-input" value={String(param.default)} readOnly disabled aria-label={`${parameterLabel(param)} ${t("toolEditor.paramDefault")}`} />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  const codePanel = (
    <div className="skill-code artifact-code">
      <div className="skill-code-head">
        <span>{t("toolEditor.code")}</span>
      </div>
      <CodeView code={code} />
      <div className="skill-code-foot">
        <span>{language}</span>
        <span>•</span>
        <span>{t("toolEditor.lines")}: {code ? code.split("\n").length : 0}</span>
        <span>•</span>
        <span>{t("toolEditor.characters")}: {code.length}</span>
      </div>
    </div>
  );

  return (
    <Modal open onClose={onClose} title={name} size="5xl" actions={saveButton}>
      {save.kind === "error" ? <div className="settings-msg err">{t("toast.toolSaveError")}: {save.message}</div> : null}
      {save.kind === "done" ? <div className="settings-msg ok">{t("toast.toolSaveSuccess")}</div> : null}
      {twoColumns ? (
        <div className="artifact-editor">
          {details}
          {codePanel}
        </div>
      ) : (
        <>
          <div className="skill-seg-tabs" role="tablist">
            {(["details", "code"] as const).map((id) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} className={`skill-seg-tab${tab === id ? " is-active" : ""}`} onClick={() => setTab(id)}>
                {id === "details" ? t("toolEditor.details") : t("toolEditor.code")}
              </button>
            ))}
          </div>
          <div className="artifact-editor is-tabbed">{tab === "details" ? details : codePanel}</div>
        </>
      )}
    </Modal>
  );
}
